<script>
import bem from '@/utils/bem';
import { mapActions, mapState } from 'vuex';
import { v4 as uuidv4 } from 'uuid';
import {
  UploadOutlined,
  QuestionCircleOutlined,
  DeleteOutlined,
  ZoomInOutlined,
  LeftOutlined,
  RightOutlined,
} from '@ant-design/icons-vue';
import toggleModal from '@/mixins/toggleModal';
import uploadFile from '@/utils/uploadFile';
import notificationWithIcon from '@/utils/notification';

const b = bem('change-product-modal');

const getDefaultWizardSettings = () => ({
  title: '',
  subtitle: '',
  description: '',
  selectionTitle: '',
  selectionDescription: '',
  showVideo: false,
  images: [],
  videos: [],
});

export default {
  components: {
    UploadOutlined,
    QuestionCircleOutlined,
    DeleteOutlined,
    ZoomInOutlined,
    LeftOutlined,
    RightOutlined,
  },
  data() {
    return {
      productInfo: {
        dependQuantityProductIds: [],
        isTaxApplied: false,
        isToggle: false,
        isMainOption: false,
        visible: false,
      },
      availableDependsList: [],
      bulkSettings: [],
      error: {
        field: '',
        text: '',
      },
      selectedCompanyId: null,
      wizardSettingsDesktop: getDefaultWizardSettings(),
      wizardSettingsMobile: getDefaultWizardSettings(),
      activeMainTab: 'basic',
      activeWizardTab: 'desktop',
      isUploadingMedia: false,
      previewVisible: false,
      previewMedia: '',
      previewType: 'image', // 'image' or 'video'
      // Sales settings for enhance products
      sales: [],
      errorSales: [],
      isShowSettingsDependOptions: false,
      displayedOptionId: null,
      idChangeableSales: null,
      productOptionsForSales: [],
      fieldHints: {
        // Basic Info
        displayName: 'Product name shown to customers',
        displayText: 'Short description of the product',
        disclaimer: 'Legal disclaimer or additional info',
        // SEO
        url: 'URL slug for the product page',
        seoTitle: 'Title for search engines (meta title)',
        seoDescription: 'Description for search engines (meta description)',
        // Pricing
        price: 'Base price in dollars',
        additionalDiscount: 'Discount amount in dollars',
        // Settings
        dependQuantityProductIds: 'Products that affect quantity calculation',
        company: 'Associated company for this product',
        // Toggles
        isTaxApplied: 'Apply tax to this product price',
        isToggle: 'Show as toggle option in wizard',
        isMainOption: 'Mark as main/primary option',
        visible: 'Show this product to customers',
        // Wizard Settings
        title: 'Main heading in the wizard option card',
        subtitle: 'Secondary text (e.g. Make any image look its best, and make sure that it is...)',
        description: 'Detailed description below the title',
        selectionTitle: 'Title in the selection confirmation block',
        selectionDescription: 'Description in the selection confirmation block',
        showVideo: 'Toggle between showing images or videos',
        images: 'Images displayed in the wizard card',
        videos: 'Videos displayed in the wizard card (when video toggle is on)',
      },
    };
  },
  props: {
    product: {
      displayName: String,
      displayText: String,
      disclaimer: String,
      price: Number,
      additionalDiscount: Number,
      isTaxApplied: Boolean,
      isToggle: Boolean,
      isMainOption: Boolean,
      visible: Boolean,
      url: String,
      seoTitle: String,
      seoDescription: String,
    },
    type: {
      type: String,
      required: true,
    },
    title: {
      type: String,
      required: true,
    },
    fullProduct: {
      type: Object,
    },
    isNewProduct: {
      type: Boolean,
      required: true,
    },
    hideFields: {
      type: Boolean,
      default: false,
    },
    availableOptions: {
      type: Array,
      default: () => [],
    },
  },
  mixins: [toggleModal],
  computed: {
    ...mapState({
      companies: (state) => state.companies.companies,
    }),
    companyOptions() {
      return [
        { value: null, label: 'None' },
        ...this.companies.map((company) => ({
          value: company.id,
          label: company.displayName,
        })),
      ];
    },
    currentWizardSettings() {
      return this.activeWizardTab === 'desktop'
        ? this.wizardSettingsDesktop
        : this.wizardSettingsMobile;
    },
    // Filter available options for sales (exclude already selected)
    filterOptionsForSales() {
      if (this.idChangeableSales === null) return [];
      const selectedIds = this.sales[this.idChangeableSales]?.dependsIds || [];
      return this.productOptionsForSales.filter(
        (option) => !selectedIds.includes(option.value),
      );
    },
    // Get selected options for current sales block
    getSelectedOptionsForSales() {
      if (this.idChangeableSales === null) return [];
      const selectedIds = this.sales[this.idChangeableSales]?.dependsIds || [];
      return this.productOptionsForSales.filter(
        (option) => selectedIds.includes(option.value),
      );
    },
  },
  async mounted() {
    await this.getCompaniesAction({ limit: 1000, offset: 0 });
    if (this.isNewProduct) {
      this.productInfo = {
        ...this.productInfo,
        displayName: '',
        displayText: '',
        disclaimer: '',
        price: 1,
        additionalDiscount: 0,
        isTaxApplied: false,
        isToggle: false,
        isMainOption: false,
        visible: true,
        url: '',
        seoTitle: '',
        seoDescription: '',
        dependQuantityProductIds: [],
      };
    } else {
      this.productInfo = {
        ...this.productInfo,
        ...this.product,
      };
    }
    this.availableDependsList = this.fullProduct ? [
      {
        value: this.fullProduct ? this.fullProduct.id : this.product.id,
        label: this.fullProduct.displayName,
      },
      ...(this.fullProduct.options || []).map((item) => ({
        value: item.id,
        label: item.displayName,
      })),
    ] : [];
    this.bulkSettings = !this.isNewProduct
    && this.product && this.product.sales && this.product.sales[0]
      ? this.product.sales[0].settings.map((item) => ({ ...item, id: uuidv4() })) : [];

    // Parse customInfo
    if (!this.isNewProduct && this.product?.customInfo) {
      try {
        const customInfo = JSON.parse(this.product.customInfo);
        this.selectedCompanyId = customInfo?.company?.companyId || null;
        // Load wizard settings
        if (customInfo?.wizardSettingsDesktop) {
          this.wizardSettingsDesktop = {
            ...getDefaultWizardSettings(),
            ...customInfo.wizardSettingsDesktop,
          };
        }
        if (customInfo?.wizardSettingsMobile) {
          this.wizardSettingsMobile = {
            ...getDefaultWizardSettings(),
            ...customInfo.wizardSettingsMobile,
          };
        }
      } catch (e) {
        this.selectedCompanyId = null;
      }
    }

    // Initialize sales for enhance products (using available options from table)
    if (this.type === 'enhance' && !this.isNewProduct) {
      // Use availableOptions prop (from table), filter out current product
      const currentProductId = this.product?.id;
      const optionsFromTable = this.availableOptions.filter(
        (opt) => opt.id !== currentProductId && opt.parentId,
      );

      this.productOptionsForSales = optionsFromTable.map((option) => ({
        value: option.id,
        label: option.displayName,
        price: option.price,
        additionalDiscount: option.additionalDiscount,
        company: this.getCompanyNameFromCustomInfo(option.customInfo),
      }));

      // Load existing sales data
      this.sales = this.product?.sales?.map((item) => ({
        ...item,
        id: item.id || uuidv4(),
        settings: item?.settings?.map((setting) => ({
          ...setting,
          id: uuidv4(),
        })) || [],
      })) || [];
    }
  },
  watch: {
    bulkSettings: {
      handler(newValue) {
        this.error = newValue.reduce((acc, item) => {
          const {
            fromQuantity,
            toQuantity,
            price,
            additionalDiscount,
          } = item;
          const isEmptyFields = fromQuantity === null
            || toQuantity === null
            || price === null
            || additionalDiscount === null;
          if (isEmptyFields) {
            return {
              field: 'all',
              text: 'One of field is empty',
            };
          }
          const isWrongQuantity = fromQuantity > toQuantity
            || newValue.some(
              (itm) => fromQuantity >= itm.fromQuantity
                && fromQuantity <= itm.toQuantity
                && itm.id !== item.id,
            )
            || newValue.some(
              (itm) => toQuantity >= itm.fromQuantity
                && fromQuantity <= itm.toQuantity
                && itm.id !== item.id,
            );
          if (isWrongQuantity) {
            return {
              field: 'quantity',
              text: 'The number overlaps with other ranges',
            };
          }
          const isWrongDiscount = item.additionalDiscount > item.price;
          if (isWrongDiscount) {
            return {
              field: 'additionalDiscount',
              text: 'Discount more than price',
            };
          }
          return acc;
        }, {
          field: '',
          text: '',
        });
        this.bulkSettings = this.bulkSettings.sort((a, c) => a.fromQuantity - c.fromQuantity);
      },
      deep: true,
    },
    sales: {
      handler(newValue) {
        console.log('=== sales watcher triggered ===');
        console.log('newValue:', JSON.parse(JSON.stringify(newValue)));
        this.errorSales = newValue.map((val) => val.settings.reduce((acc, item, index) => {
          const {
            fromQuantity,
            toQuantity,
            price,
            additionalDiscount,
          } = item;
          const isEmptyFields = fromQuantity === null
            || toQuantity === null
            || price === null
            || additionalDiscount === null;
          if (isEmptyFields) {
            return [...acc, {
              field: 'all',
              text: 'One of field is empty',
              index,
            }];
          }
          const isWrongQuantity = (
            fromQuantity > toQuantity
            || newValue.some(
              (itm) => fromQuantity >= itm.fromQuantity
                && fromQuantity <= itm.toQuantity
                && itm.id !== item.id,
            ) || newValue.some(
              (itm) => toQuantity >= itm.fromQuantity
                && fromQuantity <= itm.toQuantity
                && itm.id !== item.id,
            )
          );
          if (isWrongQuantity) {
            return [...acc, {
              field: 'quantity',
              text: 'The number overlaps with other ranges',
              index,
            }];
          }
          const isWrongDiscount = item.additionalDiscount > item.price;
          if (isWrongDiscount) {
            return [...acc, {
              field: 'additionalDiscount',
              text: 'Discount more than price',
              index,
            }];
          }
          return [...acc, {
            field: '',
            text: '',
          }];
        }, []));
        console.log('errorSales after calculation:', JSON.parse(JSON.stringify(this.errorSales)));
      },
      deep: true,
    },
  },
  methods: {
    b(firstArg, secondArg) {
      return b(firstArg, secondArg);
    },
    ...mapActions({
      updateProductAction: 'products/updateProduct',
      addProductOptionAction: 'products/addProductOption',
      updateFrameOptionAction: 'createTemplate/updateFrameOption',
      getCompaniesAction: 'companies/getCompanies',
    }),
    getCustomInfoString() {
      const customInfo = {
        company: null,
        wizardSettingsDesktop: this.wizardSettingsDesktop,
        wizardSettingsMobile: this.wizardSettingsMobile,
      };

      if (this.selectedCompanyId !== null) {
        const selectedCompany = this.companies.find((c) => c.id === this.selectedCompanyId);
        customInfo.company = {
          companyId: this.selectedCompanyId,
          companyUrl: selectedCompany?.url || '',
        };
      }

      return JSON.stringify(customInfo);
    },
    handleOk(values) {
      console.log('=== handleOk called ===');
      console.log('FormKit values:', values);
      console.log('this.sales:', JSON.parse(JSON.stringify(this.sales)));
      console.log('this.errorSales:', JSON.parse(JSON.stringify(this.errorSales)));
      console.log('this.error:', this.error);
      console.log('this.bulkSettings:', JSON.parse(JSON.stringify(this.bulkSettings)));
      const newValue = {
        ...values,
        // When hideFields is true, keep original values for hidden fields
        displayName: this.hideFields ? this.productInfo.displayName : values.displayName,
        displayText: this.hideFields ? this.productInfo.displayText || '' : values.displayText,
        disclaimer: this.hideFields ? this.productInfo.disclaimer : values.disclaimer,
        dependQuantityProductIds: this.hideFields
          ? [] : (this.productInfo.dependQuantityProductIds || []),
        id: this.productInfo.id || this.fullProduct.id,
        type: this.type,
        isTaxApplied: this.productInfo.isTaxApplied,
        isToggle: this.productInfo.isToggle,
        isMainOption: this.productInfo.isMainOption,
        visible: this.productInfo.visible,
        customInfo: this.getCustomInfoString(),
      };
      if (this.isNewProduct) {
        newValue.productType = this.type;
        this.addProductOptionAction(newValue);
      } else {
        // For enhance products, use the new sales structure with product options
        if (this.type === 'enhance') {
          newValue.sales = this.sales.map(({
            id,
            ...item
          }) => ({
            ...item,
            settings: item.settings.map(({
              id: _,
              ...rest
            }) => (rest)),
          }));
        } else {
          // Changed condition - should create sales if bulkSettings has data
          // regardless of whether productInfo.sales exists
          newValue.sales = this.bulkSettings.length > 0 && !this.isNewProduct
            ? [{
              applyToOptions: false,
              dependsIds: [this.productInfo.id],
              settings: this.bulkSettings.map(({ id: _, ...rest }) => (rest)),
            }] : [];
        }

        // Check if this is a frame option (photo_art or collage)
        if (this.type === 'photo_art' || this.type === 'collage') {
          // For frame options, use updateFrameOptionAction
          const frameOptionData = {
            type: this.type,
            productId: this.productInfo.parentId,
            optionId: this.productInfo.id,
            option: {
              displayName: values.displayName,
              price: values.price,
              additionalDiscount: values.additionalDiscount,
              inStock: this.productInfo.inStock,
              isDefaultOption: this.productInfo.isDefaultOption,
              isDefaultMinOption: this.productInfo.isDefaultMinOption,
              isPersonalize: this.productInfo.isPersonalize,
              frameColorId: this.productInfo.frameColor?.id || this.productInfo.frameColor,
              frameSizeId: this.productInfo.frameSize?.id || this.productInfo.frameSize,
              frameOrientation: this.productInfo.frameOrientation,
              isTaxApplied: true,
              personalizeImage: {
                imageKey: '',
                imageUri: '',
              },
              sales: newValue.sales,
            },
          };
          this.updateFrameOptionAction(frameOptionData);
        } else {
          // For regular products (digitize, enhance, etc.), use updateProductAction
          this.updateProductAction(newValue);
        }
      }
      this.hideModal();
    },
    onDeleteBulk(id) {
      this.bulkSettings = this.bulkSettings.filter((item) => item.id !== id);
    },
    addBulk() {
      this.bulkSettings = [
        ...this.bulkSettings,
        {
          id: uuidv4(),
          fromQuantity: 1,
          toQuantity: 1,
          price: 1,
          additionalDiscount: 0,
          isPercentage: false,
        },
      ];
    },
    async handleMediaUpload(info, platform, mediaType) {
      const { file } = info;
      const isImage = mediaType === 'images';
      const isVideo = mediaType === 'videos';

      if (isImage && !file.type.startsWith('image/')) {
        notificationWithIcon('error', 'You can only upload image files!');
        return;
      }
      if (isVideo && !file.type.startsWith('video/')) {
        notificationWithIcon('error', 'You can only upload video files!');
        return;
      }

      const maxSize = isVideo ? 50 : 5; // 50MB for video, 5MB for images
      const isLtMaxSize = file.size / 1024 / 1024 < maxSize;
      if (!isLtMaxSize) {
        notificationWithIcon('error', `File must be smaller than ${maxSize}MB!`);
        return;
      }

      try {
        this.isUploadingMedia = true;
        notificationWithIcon('info', `Uploading ${isImage ? 'image' : 'video'}...`);
        const response = await uploadFile(file, 'default');
        if (response?.data?.data?.url) {
          const settings = platform === 'desktop'
            ? this.wizardSettingsDesktop
            : this.wizardSettingsMobile;
          settings[mediaType] = [...settings[mediaType], response.data.data.url];
          notificationWithIcon('success', `${isImage ? 'Image' : 'Video'} uploaded successfully!`);
        }
      } catch (error) {
        console.error('Media upload failed:', error);
        notificationWithIcon('error', 'Upload failed');
      } finally {
        this.isUploadingMedia = false;
      }
    },
    removeMedia(platform, mediaType, index) {
      const settings = platform === 'desktop'
        ? this.wizardSettingsDesktop
        : this.wizardSettingsMobile;
      settings[mediaType] = settings[mediaType].filter((_, i) => i !== index);
    },
    openPreview(src, type) {
      this.previewMedia = src;
      this.previewType = type;
      this.previewVisible = true;
    },
    closePreview() {
      this.previewVisible = false;
      this.previewMedia = '';
    },
    // Sales methods for enhance products
    getCompanyNameFromCustomInfo(customInfo) {
      if (!customInfo) return null;
      try {
        const parsed = JSON.parse(customInfo);
        const companyId = parsed?.company?.companyId;
        if (!companyId) return null;
        const company = this.companies.find((c) => c.id === companyId);
        return company?.displayName || null;
      } catch (e) {
        return null;
      }
    },
    addSalesBlock() {
      this.sales = [
        ...this.sales,
        {
          id: uuidv4(),
          applyToOptions: false,
          dependsIds: [],
          settings: [],
          displayName: '',
        },
      ];
    },
    onDeleteSalesBlock(id) {
      this.sales = this.sales.filter((item) => item.id !== id);
    },
    addSalesInterval(salesItem, index) {
      const { settings = [] } = salesItem;
      const toQuantityPrev = settings?.[settings.length - 1]?.toQuantity || 0;

      this.sales[index].settings = [
        ...settings,
        {
          id: uuidv4(),
          fromQuantity: toQuantityPrev + 1,
          toQuantity: toQuantityPrev + 1,
          price: 1,
          additionalDiscount: 0,
          isPercentage: false,
        },
      ];
    },
    onDeleteSalesInterval(settingId, salesIndex) {
      this.sales[salesIndex].settings = this.sales[salesIndex].settings.filter(
        (item) => item.id !== settingId,
      );
    },
    showEditDependentOptions(salesItem, index) {
      this.idChangeableSales = index;
      this.isShowSettingsDependOptions = true;
    },
    closeDependentOptionsEditor() {
      this.isShowSettingsDependOptions = false;
      this.displayedOptionId = null;
      this.idChangeableSales = null;
    },
    addOptionToSales(option) {
      const { dependsIds } = this.sales[this.idChangeableSales];
      this.sales[this.idChangeableSales].dependsIds = [
        ...new Set([...dependsIds, option.value]),
      ];
    },
    addAllOptionsToSales() {
      const allOptionIds = this.productOptionsForSales.map((opt) => opt.value);
      this.sales[this.idChangeableSales].dependsIds = [
        ...new Set([
          ...this.sales[this.idChangeableSales].dependsIds,
          ...allOptionIds,
        ]),
      ];
    },
    removeOptionFromSales(option) {
      const { dependsIds } = this.sales[this.idChangeableSales];
      this.sales[this.idChangeableSales].dependsIds = dependsIds.filter(
        (id) => id !== option.value,
      );
    },
    removeAllOptionsFromSales() {
      this.sales[this.idChangeableSales].dependsIds = [];
    },
    onSubmitInvalid(e) {
      console.log('=== FormKit submit-invalid ===');
      console.log('Event:', e);
      // eslint-disable-next-line no-underscore-dangle
      const formNode = e.target || e;
      console.log('Form node:', formNode);
      // Get all invalid fields
      if (formNode && formNode.walk) {
        formNode.walk((node) => {
          if (node.context?.state?.valid === false) {
            console.log('Invalid field:', node.name, node.context?.messages);
          }
        });
      }
    },
  },
};
</script>

<template>
  <a-modal :open="show" :footer="null" width="900px" @cancel="hideModal">
    <div :class="b('modal')">
      <FormKit
        type="form"
        submit-label="Save"
        @submit="handleOk"
        @submit-invalid="onSubmitInvalid"
        :disabled="Boolean(error.text)"
      >
        <h1 class="text-2xl font-bold mb-4">{{ title }}</h1>

        <a-tabs v-model:activeKey="activeMainTab" :class="b('main-tabs')">
          <!-- Basic Info Tab -->
          <a-tab-pane key="basic" tab="Basic Info" v-if="!hideFields">
            <div :class="b('tab-content')">
              <div :class="b('row')">
                <div :class="b('input', { wide: true })">
                  <div :class="b('label-with-hint')">
                    <span>Display name</span>
                    <a-tooltip :title="fieldHints.displayName">
                      <QuestionCircleOutlined :class="b('hint-icon')" />
                    </a-tooltip>
                  </div>
                  <FormKit
                    type="text"
                    name="displayName"
                    validation="required"
                    placeholder="Display name"
                    v-model="productInfo.displayName"
                  />
                </div>
              </div>
              <div :class="b('row')">
                <div :class="b('input', { wide: true })">
                  <div :class="b('label-with-hint')">
                    <span>Disclaimer</span>
                    <a-tooltip :title="fieldHints.disclaimer">
                      <QuestionCircleOutlined :class="b('hint-icon')" />
                    </a-tooltip>
                  </div>
                  <FormKit
                    type="text"
                    name="disclaimer"
                    placeholder="Disclaimer"
                    v-model="productInfo.disclaimer"
                  />
                </div>
              </div>
              <div :class="b('row')">
                <div :class="b('input', { wide: true })">
                  <div :class="b('label-with-hint')">
                    <span>Description</span>
                    <a-tooltip :title="fieldHints.displayText">
                      <QuestionCircleOutlined :class="b('hint-icon')" />
                    </a-tooltip>
                  </div>
                  <a-textarea
                    name="displayText"
                    placeholder="Description"
                    v-model:value="productInfo.displayText"
                    :rows="3"
                  />
                </div>
              </div>

              <!-- SEO fields (for parent products) -->
              <template v-if="!isNewProduct && product && product.parentId === null">
                <a-divider>SEO</a-divider>
                <div :class="b('row')">
                  <div :class="b('input', { wide: true })">
                    <div :class="b('label-with-hint')">
                      <span>Product URL</span>
                      <a-tooltip :title="fieldHints.url">
                        <QuestionCircleOutlined :class="b('hint-icon')" />
                      </a-tooltip>
                    </div>
                    <FormKit
                      type="text"
                      name="url"
                      validation="required"
                      placeholder="Type product url"
                      v-model="productInfo.url"
                    />
                  </div>
                </div>
                <div :class="b('row')">
                  <div :class="b('input', { wide: true })">
                    <div :class="b('label-with-hint')">
                      <span>SEO Title</span>
                      <a-tooltip :title="fieldHints.seoTitle">
                        <QuestionCircleOutlined :class="b('hint-icon')" />
                      </a-tooltip>
                    </div>
                    <FormKit
                      type="text"
                      name="seoTitle"
                      placeholder="Type product seo title"
                      v-model="productInfo.seoTitle"
                    />
                  </div>
                </div>
                <div :class="b('row')">
                  <div :class="b('input', { wide: true })">
                    <div :class="b('label-with-hint')">
                      <span>SEO Description</span>
                      <a-tooltip :title="fieldHints.seoDescription">
                        <QuestionCircleOutlined :class="b('hint-icon')" />
                      </a-tooltip>
                    </div>
                    <FormKit
                      type="text"
                      name="seoDescription"
                      placeholder="Type product seo description"
                      v-model="productInfo.seoDescription"
                    />
                  </div>
                </div>
              </template>

              <!-- Settings -->
              <template v-if="!hideFields || type === 'enhance'">
                <a-divider>Settings</a-divider>
                <div :class="b('row')">
                  <div :class="b('input')" v-if="!hideFields">
                    <div :class="b('label-with-hint')">
                      <span>Depend products quantity</span>
                      <a-tooltip :title="fieldHints.dependQuantityProductIds">
                        <QuestionCircleOutlined :class="b('hint-icon')" />
                      </a-tooltip>
                    </div>
                    <a-select
                      v-model:value="productInfo.dependQuantityProductIds"
                      :options="availableDependsList"
                      mode="multiple"
                      placeholder="Please select"
                      style="width: 100%"
                    ></a-select>
                  </div>
                  <div :class="b('input')" v-if="type === 'enhance'">
                    <div :class="b('label-with-hint')">
                      <span>Company</span>
                      <a-tooltip :title="fieldHints.company">
                        <QuestionCircleOutlined :class="b('hint-icon')" />
                      </a-tooltip>
                    </div>
                    <a-select
                      v-model:value="selectedCompanyId"
                      :options="companyOptions"
                      placeholder="Select company"
                      style="width: 100%"
                    ></a-select>
                  </div>
                </div>
              </template>
            </div>
          </a-tab-pane>

          <!-- Pricing Tab -->
          <a-tab-pane key="pricing" tab="Pricing">
            <div :class="b('tab-content')">
              <div :class="b('row')">
                <div :class="b('input')">
                  <div :class="b('label-with-hint')">
                    <span>Price</span>
                    <a-tooltip :title="fieldHints.price">
                      <QuestionCircleOutlined :class="b('hint-icon')" />
                    </a-tooltip>
                  </div>
                  <FormKit
                    type="text"
                    name="price"
                    placeholder="Price"
                    prefix="dollar"
                    validation="number"
                    v-model="productInfo.price"
                  />
                </div>
                <div :class="b('input')">
                  <div :class="b('label-with-hint')">
                    <span>Additional discount</span>
                    <a-tooltip :title="fieldHints.additionalDiscount">
                      <QuestionCircleOutlined :class="b('hint-icon')" />
                    </a-tooltip>
                  </div>
                  <FormKit
                    type="text"
                    validation="number"
                    name="additionalDiscount"
                    placeholder="Additional discount"
                    prefix="dollar"
                    v-model="productInfo.additionalDiscount"
                  />
                </div>
              </div>
            </div>
          </a-tab-pane>

          <!-- Wizard Settings Tab (only for enhance) -->
          <a-tab-pane key="wizard" tab="Wizard Settings" v-if="type === 'enhance'">
            <div :class="b('tab-content')">
              <a-tabs v-model:activeKey="activeWizardTab" size="small">
                <a-tab-pane key="desktop" tab="Desktop">
                  <div :class="b('wizard-fields')">
                    <div :class="b('row')">
                      <div :class="b('input', { wide: true })">
                        <div :class="b('label-with-hint')">
                          <span>Title</span>
                          <a-tooltip :title="fieldHints.title">
                            <QuestionCircleOutlined :class="b('hint-icon')" />
                          </a-tooltip>
                        </div>
                        <a-input
                          v-model:value="wizardSettingsDesktop.title"
                          placeholder="e.g. IMAGE REMASTER & UP-RES"
                        />
                      </div>
                    </div>
                    <div :class="b('row')">
                      <div :class="b('input', { wide: true })">
                        <div :class="b('label-with-hint')">
                          <span>Subtitle</span>
                          <a-tooltip :title="fieldHints.subtitle">
                            <QuestionCircleOutlined :class="b('hint-icon')" />
                          </a-tooltip>
                        </div>
                        <a-input
                          v-model:value="wizardSettingsDesktop.subtitle"
                          placeholder="e.g. Make any image look its best..."
                        />
                      </div>
                    </div>
                    <div :class="b('row')">
                      <div :class="b('input', { wide: true })">
                        <div :class="b('label-with-hint')">
                          <span>Description</span>
                          <a-tooltip :title="fieldHints.description">
                            <QuestionCircleOutlined :class="b('hint-icon')" />
                          </a-tooltip>
                        </div>
                        <a-textarea
                          v-model:value="wizardSettingsDesktop.description"
                          placeholder="Description text..."
                          :rows="3"
                        />
                      </div>
                    </div>
                    <div :class="b('row')">
                      <div :class="b('input', { wide: true })">
                        <div :class="b('label-with-hint')">
                          <span>Selection Title</span>
                          <a-tooltip :title="fieldHints.selectionTitle">
                            <QuestionCircleOutlined :class="b('hint-icon')" />
                          </a-tooltip>
                        </div>
                        <a-input
                          v-model:value="wizardSettingsDesktop.selectionTitle"
                          placeholder="e.g. IMAGE REMASTER"
                        />
                      </div>
                    </div>
                    <div :class="b('row')">
                      <div :class="b('input', { wide: true })">
                        <div :class="b('label-with-hint')">
                          <span>Selection Description</span>
                          <a-tooltip :title="fieldHints.selectionDescription">
                            <QuestionCircleOutlined :class="b('hint-icon')" />
                          </a-tooltip>
                        </div>
                        <a-textarea
                          v-model:value="wizardSettingsDesktop.selectionDescription"
                          placeholder="Selection description..."
                          :rows="2"
                        />
                      </div>
                    </div>

                    <!-- Media Section Desktop -->
                    <a-divider>Media</a-divider>
                    <div :class="b('media-section')">
                      <div :class="b('label-with-hint')">
                        <span>Show Video</span>
                        <a-tooltip :title="fieldHints.showVideo">
                          <QuestionCircleOutlined :class="b('hint-icon')" />
                        </a-tooltip>
                        <a-switch
                          v-model:checked="wizardSettingsDesktop.showVideo"
                          style="margin-left: 8px;"
                        />
                      </div>

                      <div :class="b('media-upload')">
                        <div :class="b('label-with-hint')">
                          <span>Images</span>
                          <a-tooltip :title="fieldHints.images">
                            <QuestionCircleOutlined :class="b('hint-icon')" />
                          </a-tooltip>
                        </div>
                        <div :class="b('media-list')">
                          <div
                            v-for="(img, index) in wizardSettingsDesktop.images"
                            :key="'desktop-img-' + index"
                            :class="b('media-item')"
                          >
                            <div
                              :class="b('media-wrapper')"
                              tabindex="0"
                              @click="openPreview(img, 'image')"
                              @keydown.enter="openPreview(img, 'image')"
                            >
                              <img
                                :src="img"
                                alt="Preview"
                                :class="b('media-preview')"
                              />
                              <div :class="b('zoom-overlay')">
                                <ZoomInOutlined :class="b('zoom-icon')" />
                              </div>
                            </div>
                            <a-button
                              type="text"
                              danger
                              size="small"
                              @click="removeMedia('desktop', 'images', index)"
                            >
                              <DeleteOutlined />
                            </a-button>
                          </div>
                          <a-upload
                            name="file"
                            :show-upload-list="false"
                            :custom-request="(i) => handleMediaUpload(i, 'desktop', 'images')"
                          >
                            <a-button :loading="isUploadingMedia" size="small">
                              <template #icon><UploadOutlined /></template>
                              Add Image
                            </a-button>
                          </a-upload>
                        </div>
                      </div>

                      <div :class="b('media-upload')" v-if="wizardSettingsDesktop.showVideo">
                        <div :class="b('label-with-hint')">
                          <span>Videos</span>
                          <a-tooltip :title="fieldHints.videos">
                            <QuestionCircleOutlined :class="b('hint-icon')" />
                          </a-tooltip>
                        </div>
                        <div :class="b('media-list')">
                          <div
                            v-for="(vid, index) in wizardSettingsDesktop.videos"
                            :key="'desktop-vid-' + index"
                            :class="b('media-item')"
                          >
                            <div
                              :class="b('media-wrapper')"
                              tabindex="0"
                              @click="openPreview(vid, 'video')"
                              @keydown.enter="openPreview(vid, 'video')"
                            >
                              <video
                                :src="vid"
                                :class="b('media-preview')"
                                muted
                              />
                              <div :class="b('zoom-overlay')">
                                <ZoomInOutlined :class="b('zoom-icon')" />
                              </div>
                            </div>
                            <a-button
                              type="text"
                              danger
                              size="small"
                              @click="removeMedia('desktop', 'videos', index)"
                            >
                              <DeleteOutlined />
                            </a-button>
                          </div>
                          <a-upload
                            name="file"
                            :show-upload-list="false"
                            :custom-request="(i) => handleMediaUpload(i, 'desktop', 'videos')"
                          >
                            <a-button :loading="isUploadingMedia" size="small">
                              <template #icon><UploadOutlined /></template>
                              Add Video
                            </a-button>
                          </a-upload>
                        </div>
                      </div>
                    </div>
                  </div>
                </a-tab-pane>

                <a-tab-pane key="mobile" tab="Mobile">
                  <div :class="b('wizard-fields')">
                    <div :class="b('row')">
                      <div :class="b('input', { wide: true })">
                        <div :class="b('label-with-hint')">
                          <span>Title</span>
                          <a-tooltip :title="fieldHints.title">
                            <QuestionCircleOutlined :class="b('hint-icon')" />
                          </a-tooltip>
                        </div>
                        <a-input
                          v-model:value="wizardSettingsMobile.title"
                          placeholder="e.g. IMAGE REMASTER & UP-RES"
                        />
                      </div>
                    </div>
                    <div :class="b('row')">
                      <div :class="b('input', { wide: true })">
                        <div :class="b('label-with-hint')">
                          <span>Subtitle</span>
                          <a-tooltip :title="fieldHints.subtitle">
                            <QuestionCircleOutlined :class="b('hint-icon')" />
                          </a-tooltip>
                        </div>
                        <a-input
                          v-model:value="wizardSettingsMobile.subtitle"
                          placeholder="e.g. Make any image look its best..."
                        />
                      </div>
                    </div>
                    <div :class="b('row')">
                      <div :class="b('input', { wide: true })">
                        <div :class="b('label-with-hint')">
                          <span>Description</span>
                          <a-tooltip :title="fieldHints.description">
                            <QuestionCircleOutlined :class="b('hint-icon')" />
                          </a-tooltip>
                        </div>
                        <a-textarea
                          v-model:value="wizardSettingsMobile.description"
                          placeholder="Description text..."
                          :rows="3"
                        />
                      </div>
                    </div>
                    <div :class="b('row')">
                      <div :class="b('input', { wide: true })">
                        <div :class="b('label-with-hint')">
                          <span>Selection Title</span>
                          <a-tooltip :title="fieldHints.selectionTitle">
                            <QuestionCircleOutlined :class="b('hint-icon')" />
                          </a-tooltip>
                        </div>
                        <a-input
                          v-model:value="wizardSettingsMobile.selectionTitle"
                          placeholder="e.g. IMAGE REMASTER"
                        />
                      </div>
                    </div>
                    <div :class="b('row')">
                      <div :class="b('input', { wide: true })">
                        <div :class="b('label-with-hint')">
                          <span>Selection Description</span>
                          <a-tooltip :title="fieldHints.selectionDescription">
                            <QuestionCircleOutlined :class="b('hint-icon')" />
                          </a-tooltip>
                        </div>
                        <a-textarea
                          v-model:value="wizardSettingsMobile.selectionDescription"
                          placeholder="Selection description..."
                          :rows="2"
                        />
                      </div>
                    </div>

                    <!-- Media Section Mobile -->
                    <a-divider>Media</a-divider>
                    <div :class="b('media-section')">
                      <div :class="b('label-with-hint')">
                        <span>Show Video</span>
                        <a-tooltip :title="fieldHints.showVideo">
                          <QuestionCircleOutlined :class="b('hint-icon')" />
                        </a-tooltip>
                        <a-switch
                          v-model:checked="wizardSettingsMobile.showVideo"
                          style="margin-left: 8px;"
                        />
                      </div>

                      <div :class="b('media-upload')">
                        <div :class="b('label-with-hint')">
                          <span>Images</span>
                          <a-tooltip :title="fieldHints.images">
                            <QuestionCircleOutlined :class="b('hint-icon')" />
                          </a-tooltip>
                        </div>
                        <div :class="b('media-list')">
                          <div
                            v-for="(img, index) in wizardSettingsMobile.images"
                            :key="'mobile-img-' + index"
                            :class="b('media-item')"
                          >
                            <div
                              :class="b('media-wrapper')"
                              tabindex="0"
                              @click="openPreview(img, 'image')"
                              @keydown.enter="openPreview(img, 'image')"
                            >
                              <img
                                :src="img"
                                alt="Preview"
                                :class="b('media-preview')"
                              />
                              <div :class="b('zoom-overlay')">
                                <ZoomInOutlined :class="b('zoom-icon')" />
                              </div>
                            </div>
                            <a-button
                              type="text"
                              danger
                              size="small"
                              @click="removeMedia('mobile', 'images', index)"
                            >
                              <DeleteOutlined />
                            </a-button>
                          </div>
                          <a-upload
                            name="file"
                            :show-upload-list="false"
                            :custom-request="(i) => handleMediaUpload(i, 'mobile', 'images')"
                          >
                            <a-button :loading="isUploadingMedia" size="small">
                              <template #icon><UploadOutlined /></template>
                              Add Image
                            </a-button>
                          </a-upload>
                        </div>
                      </div>

                      <div :class="b('media-upload')" v-if="wizardSettingsMobile.showVideo">
                        <div :class="b('label-with-hint')">
                          <span>Videos</span>
                          <a-tooltip :title="fieldHints.videos">
                            <QuestionCircleOutlined :class="b('hint-icon')" />
                          </a-tooltip>
                        </div>
                        <div :class="b('media-list')">
                          <div
                            v-for="(vid, index) in wizardSettingsMobile.videos"
                            :key="'mobile-vid-' + index"
                            :class="b('media-item')"
                          >
                            <div
                              :class="b('media-wrapper')"
                              tabindex="0"
                              @click="openPreview(vid, 'video')"
                              @keydown.enter="openPreview(vid, 'video')"
                            >
                              <video
                                :src="vid"
                                :class="b('media-preview')"
                                muted
                              />
                              <div :class="b('zoom-overlay')">
                                <ZoomInOutlined :class="b('zoom-icon')" />
                              </div>
                            </div>
                            <a-button
                              type="text"
                              danger
                              size="small"
                              @click="removeMedia('mobile', 'videos', index)"
                            >
                              <DeleteOutlined />
                            </a-button>
                          </div>
                          <a-upload
                            name="file"
                            :show-upload-list="false"
                            :custom-request="(i) => handleMediaUpload(i, 'mobile', 'videos')"
                          >
                            <a-button :loading="isUploadingMedia" size="small">
                              <template #icon><UploadOutlined /></template>
                              Add Video
                            </a-button>
                          </a-upload>
                        </div>
                      </div>
                    </div>
                  </div>
                </a-tab-pane>
              </a-tabs>
            </div>
          </a-tab-pane>

          <!-- Bulk Discounts Tab -->
          <a-tab-pane key="bulk" tab="Bulk Discounts" v-if="false">
            <div :class="b('tab-content')">
              <div :class="b('column')">
                <div
                  :class="b('one-bulk')"
                  v-for="item in bulkSettings"
                  :key="item.id"
                >
                  <div :class="b('one-bulk-quantity-block')">
                    <a-input-number
                      v-model:value="item.fromQuantity"
                      :min="1"
                      :max="99999"
                      placeholder="Min"
                      :status="error.field === 'quantity' || !item.fromQuantity ? 'error' : ''"
                    />
                    <div>-</div>
                    <a-input-number
                      v-model:value="item.toQuantity"
                      :min="1"
                      :max="99999"
                      placeholder="Max"
                      :status="error.field === 'quantity' || !item.toQuantity ? 'error' : ''"
                    />
                  </div>
                  <div :class="b('one-bulk-price-block')">
                    <div :class="b('one-bulk-price-title')">Price</div>
                    <a-input-number
                      v-model:value="item.price"
                      :min="1"
                      :max="99999"
                      prefix="$"
                      placeholder="Price"
                      :status="!item.price ? 'error' : ''"
                    />
                  </div>
                  <div :class="b('one-bulk-price-block')">
                    <div :class="b('one-bulk-price-title')">Discount</div>
                    <a-input-number
                      v-model:value="item.additionalDiscount"
                      :min="0"
                      :max="99999"
                      prefix="$"
                      placeholder="Discount"
                      :status="error.field === 'additionalDiscount' ? 'error' : ''"
                    />
                  </div>
                  <div :class="b('one-bulk-percent-block')">
                    <div :class="b('one-bulk-percent-title')">%</div>
                    <a-switch v-model:checked="item.isPercentage" size="small"></a-switch>
                  </div>
                  <a-button type="text" danger @click="onDeleteBulk(item.id)">
                    <DeleteOutlined />
                  </a-button>
                </div>
                <div :class="b('error')" v-if="error.text">{{ error.text }}</div>
                <a-button type="primary" @click="addBulk">+ Add bulk discount</a-button>
              </div>
            </div>
          </a-tab-pane>

          <!-- Sales Tab (for enhance products) -->
          <a-tab-pane key="sales" tab="Sales" v-if="type === 'enhance' && !isNewProduct">
            <div :class="b('tab-content')">
              <!-- Options Editor Modal View -->
              <div v-if="isShowSettingsDependOptions" :class="b('options-editor')">
                <div :class="b('options-editor-header')">
                  <h3>Select product options for Sales {{ idChangeableSales + 1 }}</h3>
                  <a-button type="primary" @click="closeDependentOptionsEditor">
                    Close editor
                  </a-button>
                </div>
                <div :class="b('options-selector-wrapper')">
                  <!-- Available options -->
                  <div :class="b('options-column')">
                    <h4>Available Options</h4>
                    <a-button
                      v-if="filterOptionsForSales.length"
                      :class="b('all-options-btn')"
                      @click="addAllOptionsToSales"
                    >
                      Add All
                      <RightOutlined />
                    </a-button>
                    <div :class="b('options-list')">
                      <a-button
                        v-for="option in filterOptionsForSales"
                        :key="option.value"
                        :class="b('option-item')"
                        type="dashed"
                        @click="addOptionToSales(option)"
                      >
                        {{ option.label }} (${{ option.price }})
                        <span v-if="option.company" :class="b('option-company')">
                          [{{ option.company }}]
                        </span>
                        <RightOutlined />
                      </a-button>
                    </div>
                  </div>
                  <!-- Selected options -->
                  <div :class="b('options-column')">
                    <h4>Selected Options</h4>
                    <a-button
                      v-if="getSelectedOptionsForSales.length"
                      :class="b('all-options-btn')"
                      danger
                      @click="removeAllOptionsFromSales"
                    >
                      <LeftOutlined />
                      Remove All
                    </a-button>
                    <div :class="b('options-list')">
                      <a-button
                        v-for="option in getSelectedOptionsForSales"
                        :key="option.value"
                        :class="b('option-item')"
                        type="dashed"
                        @click="removeOptionFromSales(option)"
                      >
                        <LeftOutlined />
                        {{ option.label }} (${{ option.price }})
                        <span v-if="option.company" :class="b('option-company')">
                          [{{ option.company }}]
                        </span>
                      </a-button>
                    </div>
                  </div>
                </div>
              </div>
              <!-- Sales List View -->
              <div v-else :class="b('sales-section')">
                <div
                  v-for="(salesItem, salesIndex) in sales"
                  :key="salesItem.id"
                  :class="b('sales-block')"
                >
                  <div :class="b('sales-block-header')">
                    <a-input
                      v-model:value="salesItem.displayName"
                      :placeholder="'Sale ' + (salesIndex + 1)"
                      :class="b('sales-name-input')"
                    />
                    <a-button
                      type="primary"
                      danger
                      shape="round"
                      size="small"
                      @click="onDeleteSalesBlock(salesItem.id)"
                    >
                      <template #icon>
                        <DeleteOutlined />
                      </template>
                    </a-button>
                  </div>
                  <!-- Edit dependent options button -->
                  <div :class="b('depends-options')">
                    <a-button
                      :class="b('edit-options-btn')"
                      danger
                      @click="showEditDependentOptions(salesItem, salesIndex)"
                    >
                      Click to edit dependent options
                      ({{ salesItem.dependsIds?.length || 0 }} selected)
                    </a-button>
                  </div>
                  <!-- Intervals -->
                  <template
                    v-for="(setting, settingIndex) in salesItem.settings"
                    :key="setting.id"
                  >
                    <div :class="b('sales-interval')">
                      <div :class="b('one-bulk-quantity-block')">
                        <a-input-number
                          v-model:value="setting.fromQuantity"
                          :min="
                            (salesItem.settings?.[(settingIndex || 2) - 1]?.toQuantity || 0) + 1
                          "
                          :max="99999"
                          placeholder="Min"
                          :status="
                            errorSales[salesIndex]?.[settingIndex]?.field === 'quantity'
                              || setting.fromQuantity === null ? 'error' : ''
                          "
                        />
                        <div>-</div>
                        <a-input-number
                          v-model:value="setting.toQuantity"
                          :min="
                            (salesItem.settings?.[(settingIndex || 2) - 1]?.toQuantity || 0) + 1
                          "
                          :max="99999"
                          placeholder="Max"
                          :status="
                            errorSales[salesIndex]?.[settingIndex]?.field === 'quantity'
                            || setting.toQuantity === null ? 'error' : ''
                          "
                        />
                      </div>
                      <div :class="b('one-bulk-price-block')">
                        <div :class="b('one-bulk-price-title')">Price</div>
                        <a-input-number
                          v-model:value="setting.price"
                          :min="0"
                          :max="99999"
                          prefix="$"
                          placeholder="Price"
                          :status="setting.price === null ? 'error' : ''"
                        />
                      </div>
                      <div :class="b('one-bulk-price-block')">
                        <div :class="b('one-bulk-price-title')">Discount</div>
                        <a-input-number
                          v-model:value="setting.additionalDiscount"
                          :min="0"
                          :max="99999"
                          prefix="$"
                          placeholder="Discount"
                          :status="
                            errorSales[salesIndex]?.[settingIndex]?.field === 'additionalDiscount'
                            || setting.additionalDiscount === null ? 'error' : ''
                          "
                        />
                      </div>
                      <div :class="b('one-bulk-percent-block')">
                        <div :class="b('one-bulk-percent-title')">%</div>
                        <a-switch v-model:checked="setting.isPercentage" size="small" />
                      </div>
                      <a-button
                        type="primary"
                        danger
                        @click="onDeleteSalesInterval(setting.id, salesIndex)"
                      >
                        Delete
                      </a-button>
                    </div>
                    <div
                      v-if="errorSales[salesIndex]?.[settingIndex]?.text"
                      :class="b('error')"
                    >
                      {{ errorSales[salesIndex][settingIndex].text }}
                    </div>
                  </template>
                  <a-button
                    type="dashed"
                    @click="addSalesInterval(salesItem, salesIndex)"
                  >
                    + Interval
                  </a-button>
                </div>
                <a-button type="dashed" @click="addSalesBlock">
                  + Add Sales Block
                </a-button>
              </div>
            </div>
          </a-tab-pane>

          <!-- Options Tab -->
          <a-tab-pane key="options" tab="Options">
            <div :class="b('tab-content')">
              <div :class="b('row')">
                <div :class="b('toggle')">
                  <div :class="b('label-with-hint')">
                    <span>Is tax applied</span>
                    <a-tooltip :title="fieldHints.isTaxApplied">
                      <QuestionCircleOutlined :class="b('hint-icon')" />
                    </a-tooltip>
                  </div>
                  <a-switch v-model:checked="productInfo.isTaxApplied"></a-switch>
                </div>
                <div :class="b('toggle')">
                  <div :class="b('label-with-hint')">
                    <span>Is toggle</span>
                    <a-tooltip :title="fieldHints.isToggle">
                      <QuestionCircleOutlined :class="b('hint-icon')" />
                    </a-tooltip>
                  </div>
                  <a-switch v-model:checked="productInfo.isToggle"></a-switch>
                </div>
                <div :class="b('toggle')">
                  <div :class="b('label-with-hint')">
                    <span>Is main product</span>
                    <a-tooltip :title="fieldHints.isMainOption">
                      <QuestionCircleOutlined :class="b('hint-icon')" />
                    </a-tooltip>
                  </div>
                  <a-switch v-model:checked="productInfo.isMainOption"></a-switch>
                </div>
                <div :class="b('toggle')">
                  <div :class="b('label-with-hint')">
                    <span>Is show</span>
                    <a-tooltip :title="fieldHints.visible">
                      <QuestionCircleOutlined :class="b('hint-icon')" />
                    </a-tooltip>
                  </div>
                  <a-switch v-model:checked="productInfo.visible"></a-switch>
                </div>
              </div>
            </div>
          </a-tab-pane>
        </a-tabs>
      </FormKit>
    </div>
  </a-modal>

  <!-- Media Preview Modal -->
  <a-modal
    :open="previewVisible"
    :footer="null"
    :width="800"
    @cancel="closePreview"
    :class="b('preview-modal')"
  >
    <img
      v-if="previewType === 'image'"
      :src="previewMedia"
      alt="Preview"
      :class="b('preview-image')"
    />
    <!-- eslint-disable-next-line vuejs-accessibility/media-has-caption -->
    <video
      v-else
      :src="previewMedia"
      :class="b('preview-video')"
      controls
      autoplay
    />
  </a-modal>
</template>

<style scoped lang="scss">
@import "@/styles/variables";

// Override FormKit wrapper styles
:deep(.formkit-outer) {
  width: 100%;
}

:deep(.formkit-wrapper) {
  width: 100%;
  max-width: 100%;
}

:deep(.formkit-inner) {
  width: 100%;
}

:deep(.formkit-input) {
  width: 100%;
}

.change-product-modal {
  &__section {
    margin-bottom: 24px;
    padding-bottom: 16px;
    border-bottom: 1px solid #f0f0f0;

    &:last-child {
      border-bottom: none;
    }
  }

  &__section-title {
    font-size: 14px;
    font-weight: 600;
    color: #333;
    margin-bottom: 12px;
  }

  &__row {
    display: flex;
    align-items: flex-start;
    justify-content: flex-start;
    gap: 16px;
    margin-bottom: 12px;
    flex-wrap: wrap;
  }

  &__column {
    margin-bottom: 20px;
  }

  &__input {
    width: 100%;
    display: flex;
    flex-direction: column;

    &--wide {
      width: 100%;
    }
  }

  &__toggle {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    min-width: 120px;
  }

  &__label-with-hint {
    display: flex;
    align-items: center;
    gap: 4px;
    margin-bottom: 4px;
    font-size: 13px;
    color: #666;
  }

  &__hint-icon {
    color: #999;
    font-size: 12px;
    cursor: help;

    &:hover {
      color: #1890ff;
    }
  }

  &__main-tabs {
    margin-bottom: 16px;
  }

  &__tab-content {
    padding: 8px 0;
    min-height: 200px;
  }

  &__wizard-fields {
    padding: 12px 0;
  }

  &__media-section {
    margin-top: 16px;
    padding-top: 16px;
    border-top: 1px dashed #e8e8e8;
  }

  &__media-upload {
    margin-top: 12px;
  }

  &__media-list {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    align-items: center;
    margin-top: 8px;
  }

  &__media-item {
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
  }

  &__media-wrapper {
    position: relative;
    cursor: pointer;
    border-radius: 4px;
    overflow: hidden;

    &:hover {
      .change-product-modal__zoom-overlay {
        opacity: 1;
      }
    }

    &:focus {
      outline: 2px solid #1890ff;
      outline-offset: 2px;
    }
  }

  &__zoom-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(0, 0, 0, 0.3);
    opacity: 0;
    transition: opacity 0.2s;
    pointer-events: none;
  }

  &__zoom-icon {
    font-size: 32px;
    color: #fff;
    filter: drop-shadow(0 0 2px rgba(0, 0, 0, 0.8));
  }

  &__media-preview {
    width: 200px;
    height: 150px;
    object-fit: cover;
    border-radius: 4px;
    border: 1px solid #d9d9d9;
    display: block;
  }

  &__preview-image {
    width: 100%;
    height: 600px;
    object-fit: contain;
  }

  &__preview-video {
    width: 100%;
    height: 600px;
    object-fit: contain;
  }

  &__one-bulk {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    margin-bottom: 20px;
  }

  &__one-bulk-quantity-block {
    display: flex;
    align-items: center;
    justify-content: flex-start;
  }

  &__error {
    color: $burnt-sienna-color;
  }

  // Sales section styles
  &__sales-section {
    display: flex;
    flex-direction: column;
    gap: 20px;
  }

  &__sales-block {
    display: flex;
    flex-direction: column;
    padding: 20px;
    border-radius: 10px;
    background-color: #d1e6f6;
    gap: 10px;
  }

  &__sales-block-header {
    display: flex;
    gap: 10px;
    align-items: center;

    h3 {
      margin: 0;
    }
  }

  &__sales-name-input {
    max-width: 200px;
    font-weight: 600;
  }

  &__sales-interval {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    margin-bottom: 10px;
    padding: 10px;
    background-color: #7eb4d3;
    border-radius: 8px;
  }

  &__depends-options {
    margin: 10px 0;
  }

  &__edit-options-btn {
    width: 100%;
  }

  // Options editor styles
  &__options-editor {
    min-height: 300px;
  }

  &__options-editor-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;

    h3 {
      margin: 0;
    }
  }

  &__options-selector-wrapper {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 20px;
  }

  &__options-column {
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 15px;
    border-radius: 10px;
    background-color: #d1e6f6;
    min-height: 200px;

    h4 {
      margin: 0 0 10px;
    }
  }

  &__options-list {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  &__option-item {
    text-align: left;
  }

  &__option-company {
    color: #1890ff;
    font-size: 11px;
    margin-left: 4px;
  }

  &__all-options-btn {
    margin-bottom: 10px;
  }

  &__one-bulk-price-block {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
  }

  &__one-bulk-price-title {
    font-size: 12px;
    color: #666;
  }

  &__one-bulk-percent-block {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
  }

  &__one-bulk-percent-title {
    font-size: 12px;
    color: #666;
  }
}
</style>
